import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  PRAYERS,
  surahOfBuilding,
  type Building,
  type RegionKind,
  type RegionNode,
  type RegionTree,
} from '@map/shared';
import {
  archiveRegion,
  createRegion,
  fetchTasks,
  moveRegion,
  reweight,
  setTaskStatus,
  updatePlanet,
  updateRegion,
} from './api.js';
import { BuildingCard } from '../site/BuildingCard.js';
import { SalahLog, SalahPriority, formatRating, ratingColour } from './Salah.js';
import { PersonCard } from './PersonCard.js';
import { FacultyCard } from './FacultyCard.js';
import { ForestCard, ReadingCard } from './ArabicCards.js';
import { BookCard } from './BookCard.js';
import { StrengthCard } from './StrengthCard.js';
import { metalOf } from '../globe/Medals.js';
import { healthColor } from '../styles/tokens.js';

/**
 * The flyout for whatever is open: the planet itself, or one place on it.
 * Every level has the same two faces — Data, the buildings standing anywhere
 * inside it, and Settings, where the place and the places within it are made,
 * renamed, reshaped and removed.
 */
export interface PlacePanelProps {
  tree: RegionTree;
  /** Null is the planet itself. */
  region: RegionNode | null;
  buildings: Building[];
  unplaced: Array<{ id: string; reason: string }>;
  onSelect: (id: string | null) => void;
  onClose: () => void;
  /** Opened by clicking its camel: the one to show first, already unfolded. */
  focusBuildingId?: string | null;
  /** One thing inside the focused building, e.g. the word of a clicked tree. */
  focusItemId?: number | null;
}

type Tab = 'data' | 'settings';

/** Prayers are listed in the order they are prayed, not alphabetically. */
const PRAYER_ORDER = PRAYERS.map((p) => p.key);

const CHILD_KIND: Record<RegionKind, RegionKind | null> = {
  continent: 'country',
  country: 'city',
  // Districts exist under some cities, but are not something to manage for now.
  city: null,
  district: null,
};

const PLURAL: Record<RegionKind, string> = {
  continent: 'Continents',
  country: 'Countries',
  city: 'Cities',
  district: 'Districts',
};

/** Every region id at or below `id`. */
function subtree(tree: RegionTree, id: string): Set<string> {
  const out = new Set<string>();
  const walk = (at: string) => {
    out.add(at);
    for (const child of tree.regions[at]?.children ?? []) walk(child);
  };
  walk(id);
  return out;
}

/** Where a place could move to: any region one step shallower, minus its own parent and itself. */
function destinationsFor(tree: RegionTree, region: RegionNode): Array<{ id: string; path: string }> {
  const inside = subtree(tree, region.id);
  const out: Array<{ id: string; path: string }> = [];
  for (const candidate of Object.values(tree.regions)) {
    if (inside.has(candidate.id)) continue;
    if (candidate.id === region.parentId) continue;
    if (candidate.depth !== region.depth - 1) continue;
    out.push({ id: candidate.id, path: pathOf(tree, candidate).join(' › ') });
  }
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

function pathOf(tree: RegionTree, region: RegionNode): string[] {
  const names: string[] = [];
  for (let node: RegionNode | undefined = region; node; node = node.parentId ? tree.regions[node.parentId] : undefined) {
    names.unshift(node.name);
  }
  return names;
}

/** A fresh seed for a new shape. Stored, so the shape is the same everywhere from then on. */
function freshSeed(): number {
  return 1 + Math.floor(Math.random() * 2_000_000_000);
}

export function PlacePanel({
  tree,
  region,
  buildings,
  unplaced,
  onSelect,
  onClose,
  focusBuildingId = null,
  focusItemId = null,
}: PlacePanelProps) {
  const [tab, setTab] = useState<Tab>('data');

  const ancestry: RegionNode[] = region ? pathOfNodes(tree, region) : [];
  const title = region?.name ?? tree.planet.name;

  return (
    <aside
      className="flex h-full w-full flex-col overflow-hidden"
      style={{ background: 'var(--surface-0)', borderLeft: '1px solid var(--border)' }}
      aria-label={`${title} details`}
    >
      <div className="flex flex-col gap-3 px-6 pt-6">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-text-muted">
          <button
            onClick={() => onSelect(null)}
            className="underline-offset-2 hover:underline"
            style={{ color: region ? undefined : 'var(--text)' }}
          >
            {tree.planet.name}
          </button>
          {ancestry.map((node) => (
            <span key={node.id} className="flex items-center gap-1">
              <span aria-hidden>›</span>
              <button
                onClick={() => onSelect(node.id)}
                className="underline-offset-2 hover:underline"
                style={{ color: node.id === region?.id ? 'var(--text)' : undefined }}
              >
                {node.name}
              </button>
            </span>
          ))}
          <button onClick={onClose} className="ml-auto text-text-muted hover:text-text" aria-label="Close">
            ✕
          </button>
        </nav>

        <header className="flex items-baseline gap-3">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ background: healthColor(region ? region.health.combined : worstOf(tree)) }}
            aria-hidden
          />
          <h2 className="truncate text-xl">{title}</h2>
          <span className="text-xs text-text-muted">{region?.kind ?? 'planet'}</span>
        </header>

        <div role="tablist" className="flex gap-4 border-b border-border text-sm">
          {(['data', 'settings'] as const).map((name) => (
            <button
              key={name}
              role="tab"
              aria-selected={tab === name}
              onClick={() => setTab(name)}
              className="-mb-px border-b-2 pb-2 capitalize"
              style={{
                borderColor: tab === name ? 'var(--accent)' : 'transparent',
                color: tab === name ? 'var(--text)' : 'var(--text-muted)',
              }}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        {tab === 'data' ? (
          <DataTab tree={tree} region={region} buildings={buildings} focusBuildingId={focusBuildingId} focusItemId={focusItemId} />
        ) : (
          <SettingsTab
            key={region?.id ?? 'planet'}
            tree={tree}
            region={region}
            buildings={buildings}
            unplaced={unplaced}
            onSelect={onSelect}
          />
        )}
      </div>
    </aside>
  );
}

function pathOfNodes(tree: RegionTree, region: RegionNode): RegionNode[] {
  const out: RegionNode[] = [];
  for (let node: RegionNode | undefined = region; node; node = node.parentId ? tree.regions[node.parentId] : undefined) {
    out.unshift(node);
  }
  return out;
}

function worstOf(tree: RegionTree): number {
  return Math.max(0, ...tree.roots.map((id) => tree.regions[id]?.health.combined ?? 0));
}

/** Buildings standing anywhere inside `region`, or on the whole planet. */
function buildingsWithin(tree: RegionTree, region: RegionNode | null, buildings: Building[]): Building[] {
  const inside = region ? subtree(tree, region.id) : null;
  return buildings
    .filter((b) => tree.regions[b.regionId] && (!inside || inside.has(b.regionId)))
    .sort((a, b) =>
      a.prayer && b.prayer
        ? PRAYER_ORDER.indexOf(a.prayer) - PRAYER_ORDER.indexOf(b.prayer)
        : a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
}

/**
 * Where a building stands, told by the first place above it with a name of
 * its own — a plot is usually named after its building, which says nothing.
 * Null once that would only be the place already open.
 */
function locate(tree: RegionTree, building: Building, open: RegionNode | null): RegionNode | null {
  for (
    let node = tree.regions[building.regionId];
    node && node.id !== open?.id;
    node = node.parentId ? tree.regions[node.parentId] : undefined
  ) {
    if (node.name !== building.name) return node;
  }
  return null;
}

// ------------------------------------------------------------------- data --

function DataTab({
  tree,
  region,
  buildings,
  focusBuildingId = null,
  focusItemId = null,
}: {
  tree: RegionTree;
  region: RegionNode | null;
  buildings: Building[];
  focusBuildingId?: string | null;
  focusItemId?: number | null;
}) {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(focusBuildingId);
  const found = buildingsWithin(tree, region, buildings);
  const allBooks = found.length > 0 && found.every((b) => b.kind === 'book');
  // A shelf lists its books newest first, the way they were finished.
  const within = allBooks ? [...found].sort((a, b) => (b.book?.order ?? 0) - (a.book?.order ?? 0)) : found;

  /**
   * Arriving from a camel: its surah is the one that opens, and the list snaps
   * to it — a plot can hold a list far longer than the panel is tall.
   *
   * The scroll used to hang on the id alone, which meant it only worked if the
   * row already existed at that moment. Clicking a camel on a plot the panel
   * had not listed yet — a different place, or one whose buildings were still
   * loading — set the id, found no row, and never tried again, so the surah
   * was open somewhere far down a list you had to go and find. It now waits
   * for the row to exist, and puts it in the middle rather than merely inside
   * the edge.
   */
  const focused = useRef<HTMLLIElement>(null);
  const snapped = useRef<string | null>(null);
  useEffect(() => {
    if (!focusBuildingId) {
      snapped.current = null;
      return;
    }
    setOpenId(focusBuildingId);
    const row = focused.current;
    if (!row || snapped.current === focusBuildingId) return;
    snapped.current = focusBuildingId;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusBuildingId, within.length, region?.id]);

  const allCamels = within.length > 0 && within.every((b) => surahOfBuilding(b.name) !== null);
  const allPeople = within.length > 0 && within.every((b) => b.kind === 'person');

  const tasks = useQuery({
    queryKey: ['tasks', region?.id],
    queryFn: () => fetchTasks({ regionId: region?.id as string }),
    enabled: region !== null,
  });

  const complete = useMutation({
    mutationFn: (id: string) => setTaskStatus(id, 'done'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', region?.id] });
      await queryClient.invalidateQueries({ queryKey: ['tree'] });
    },
  });

  const openTasks = tasks.data?.tasks ?? [];

  // A prayer's own country is its log; there is nothing else to list.
  const [only] = within;
  if (region && within.length === 1 && only?.kind === 'salah') {
    return <SalahLog building={only} />;
  }
  // So is a person's own plot: they are the whole of it.
  if (region && within.length === 1 && only?.kind === 'person') {
    return <PersonCard building={only} />;
  }
  // A faculty's country is its quiz score and the weather it makes.
  if (region && within.length === 1 && only?.kind === 'faculty') {
    return <FacultyCard building={only} />;
  }
  // Arabic's two countries are their one building each: the forest, the oasis.
  if (region && within.length === 1 && only?.kind === 'forest') {
    return <ForestCard building={only} focusWordId={only.id === focusBuildingId ? focusItemId : null} />;
  }
  if (region && within.length === 1 && only?.kind === 'reading') {
    return <ReadingCard building={only} />;
  }
  // Strength of self is its count and its calendar, and nothing else.
  if (region && within.length === 1 && only?.kind === 'strength') {
    return <StrengthCard building={only} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        {/* Call them what they are on the ground. A surah is drawn as a camel
            and clicked as a camel, so a heading calling it a building is the
            panel using a word the world does not. Anywhere the list is not all
            surahs, they are still buildings. */}
        <h3 className="mb-2 text-xs uppercase tracking-wide text-text-muted">
          {allCamels ? 'Camels' : allPeople ? 'People' : allBooks ? 'Books' : 'Buildings'} ({within.length})
        </h3>
        {within.length === 0 && <p className="text-sm text-text-muted">Nothing built here yet.</p>}
        <ul className="flex flex-col">
          {within.map((building) => {
            const where = locate(tree, building, region);
            const open = openId === building.id;
            const isFocus = building.id === focusBuildingId;
            return (
              <li key={building.id} {...(isFocus ? { ref: focused } : {})}>
                <button
                  onClick={() => setOpenId(open ? null : building.id)}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-sm)] px-2 py-1.5 text-left hover:bg-surface-1"
                  aria-expanded={open}
                  style={isFocus ? { background: 'var(--surface-1)', boxShadow: 'inset 2px 0 0 var(--accent)' } : undefined}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: healthColor(building.weathering) }}
                    aria-hidden
                  />
                  <span className="flex-1 truncate text-sm">{building.name}</span>
                  {building.strength ? (
                    <span
                      className="text-xs tabular-nums"
                      style={{ color: building.strength.count >= 40 ? 'var(--strength-gold)' : 'var(--strength-held)' }}
                    >
                      day {building.strength.count}
                    </span>
                  ) : building.book ? (
                    <span className="text-xs" style={{ color: `var(--medal-${metalOf(building.book.rating)})` }} aria-label={`${building.book.rating} of 5 stars`}>
                      {'★'.repeat(building.book.rating)}
                    </span>
                  ) : building.rating !== null ? (
                    <span className="text-xs tabular-nums" style={{ color: ratingColour(building.rating) }}>
                      {formatRating(building.rating)} / 5
                    </span>
                  ) : (
                    where && <span className="max-w-[45%] truncate text-xs text-text-muted">{where.name}</span>
                  )}
                </button>
                {open && (
                  <div className="px-2 pb-2 pt-1">
                    {building.kind === 'salah' ? (
                      <SalahLog building={building} />
                    ) : building.kind === 'person' ? (
                      <PersonCard building={building} />
                    ) : building.kind === 'faculty' ? (
                      <FacultyCard building={building} />
                    ) : building.kind === 'forest' ? (
                      <ForestCard building={building} focusWordId={isFocus ? focusItemId : null} />
                    ) : building.kind === 'reading' ? (
                      <ReadingCard building={building} />
                    ) : building.kind === 'book' ? (
                      <BookCard building={building} />
                    ) : building.kind === 'strength' ? (
                      <StrengthCard building={building} />
                    ) : (
                      <BuildingCard building={building} />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {openTasks.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs uppercase tracking-wide text-text-muted">Open tasks ({openTasks.length})</h3>
          <ul className="flex flex-col gap-1">
            {openTasks.map((task) => (
              <li key={task.id} className="flex items-start gap-2 text-sm">
                <button
                  onClick={() => complete.mutate(task.id)}
                  disabled={complete.isPending}
                  aria-label={`Complete ${task.title}`}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-border hover:border-accent"
                />
                <span className="flex-1">
                  {task.title}
                  {task.targetDate && <span className="text-text-muted"> · {task.targetDate}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// --------------------------------------------------------------- settings --

interface SettingsProps {
  tree: RegionTree;
  region: RegionNode | null;
  buildings: Building[];
  unplaced: Array<{ id: string; reason: string }>;
  onSelect: (id: string | null) => void;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['tree'] });
    await queryClient.invalidateQueries({ queryKey: ['planet-buildings'] });
    await queryClient.invalidateQueries({ queryKey: ['universe'] });
  };
}

function SettingsTab({ tree, region, buildings, unplaced, onSelect }: SettingsProps) {
  const refresh = useRefresh();

  const rename = useMutation({
    mutationFn: async (name: string) => {
      if (region) await updateRegion(region.id, { version: region.version, name });
      else await updatePlanet(tree.planet.id, { name });
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => archiveRegion(id, true),
    onSuccess: refresh,
  });

  const childKind: RegionKind | null = region ? CHILD_KIND[region.kind] : 'continent';
  const children = (region ? region.children : tree.roots)
    .map((id) => tree.regions[id])
    .filter((c): c is RegionNode => c !== undefined);

  const confirmRemove = (target: RegionNode, after?: () => void) => {
    const count = buildingsWithin(tree, target, buildings).length;
    const inside = target.children.length;
    const detail = [
      inside > 0 ? `${inside} place${inside === 1 ? '' : 's'} inside it` : null,
      count > 0 ? `${count} building${count === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    const message = detail.length
      ? `Delete ${target.name}? ${detail.join(' and ')} go with it.`
      : `Delete ${target.name}?`;
    if (window.confirm(message)) remove.mutate(target.id, { onSuccess: () => after?.() });
  };

  return (
    <div className="flex flex-col gap-7">
      <Field label="Name">
        <NameInput value={region?.name ?? tree.planet.name} onSave={(name) => rename.mutate(name)} />
      </Field>

      {region && <ShapeControls tree={tree} region={region} />}

      {childKind && (
        <ChildList
          tree={tree}
          parent={region}
          kind={childKind}
          items={children}
          unplaced={unplaced}
          onSelect={onSelect}
          onRemove={(child) => confirmRemove(child)}
        />
      )}

      {region && buildingsWithin(tree, region, buildings).some((b) => b.kind === 'salah') && <SalahPriority />}

      {region && destinationsFor(tree, region).length > 0 && <MoveSection tree={tree} region={region} />}

      {region && (
        <section className="flex flex-col gap-2 border-t border-border pt-5">
          <button
            onClick={() => confirmRemove(region, () => onSelect(region.parentId))}
            disabled={remove.isPending}
            className="self-start rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1"
            style={{ color: 'var(--danger, var(--text))' }}
          >
            Delete {region.kind}
          </button>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-text-muted">{label}</h3>
      {children}
    </section>
  );
}

/** Saves on Enter or on leaving the field; Escape puts it back. */
function NameInput({ value, onSave }: { value: string; onSave: (name: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    const name = draft.trim();
    if (name && name !== value) onSave(name);
    else setDraft(value);
  };

  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      maxLength={120}
      className="rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1.5 text-sm"
    />
  );
}

/**
 * Size, then a new shape. A continent's size is its coastline scaled; anything
 * below it is a share of its parent, which is what its weight already means.
 * Nothing standing on the place moves with it except to follow the new outline.
 */
function ShapeControls({ tree, region }: { tree: RegionTree; region: RegionNode }) {
  const refresh = useRefresh();
  const isContinent = region.parentId === null;
  const siblings = isContinent
    ? []
    : (tree.regions[region.parentId as string]?.children ?? [])
        .filter((id) => id !== region.id)
        .map((id) => tree.regions[id])
        .filter((r): r is RegionNode => r !== undefined);
  const others = siblings.reduce((sum, r) => sum + r.importanceWeight, 0);
  const alone = !isContinent && siblings.length === 0;

  // Percent: of the drawn coastline for a continent, of the parent otherwise.
  const current = isContinent ? Math.round(region.geometryScale * 100) : Math.round(region.share * 100);
  const [size, setSize] = useState(current);
  useEffect(() => setSize(current), [current]);

  const generate = useMutation({
    mutationFn: async () => {
      if (isContinent) {
        await updateRegion(region.id, {
          version: region.version,
          geometrySeed: freshSeed(),
          geometryScale: size / 100,
        });
        return;
      }
      let version = region.version;
      if (!alone && size !== current) {
        // share = w / (w + others)  →  w = share · others / (1 − share)
        const share = size / 100;
        const weight = Math.min(1000, Math.max(0.001, (share * others) / (1 - share)));
        await reweight([{ id: region.id, weight: Number(weight.toFixed(3)) }]);
        version += 1;
      }
      await updateRegion(region.id, { version, geometrySeed: freshSeed() });
    },
    onSuccess: refresh,
  });

  const [min, max] = isContinent ? [40, 160] : [5, 90];

  return (
    <Field label="Shape">
      <label className="flex items-center gap-3 text-sm">
        <span className="w-10 text-text-muted">Size</span>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={Math.min(max, Math.max(min, size))}
          disabled={alone}
          onChange={(e) => setSize(Number(e.target.value))}
          className="flex-1"
          aria-label={`Size of ${region.name}`}
        />
        <span className="max-w-[9rem] shrink-0 truncate whitespace-nowrap text-right text-xs tabular-nums text-text-muted">
          {alone
            ? `all of ${tree.regions[region.parentId as string]?.name ?? 'it'}`
            : isContinent
              ? `${size}% of drawn`
              : `${size}% of ${tree.regions[region.parentId as string]?.name ?? 'parent'}`}
        </span>
      </label>
      <div className="flex items-center gap-3">
        <button
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1"
        >
          {generate.isPending ? 'Generating…' : '↻ Regenerate'}
        </button>
        <span className="text-xs text-text-muted">
          {isContinent ? 'New coastline at this size.' : 'New borders here and with its neighbours.'}
        </span>
      </div>
      {generate.isError && <p className="text-xs text-text-muted">That did not save: {generate.error.message}</p>}
    </Field>
  );
}

function ChildList({
  tree,
  parent,
  kind,
  items,
  unplaced,
  onSelect,
  onRemove,
}: {
  tree: RegionTree;
  parent: RegionNode | null;
  kind: RegionKind;
  items: RegionNode[];
  unplaced: Array<{ id: string; reason: string }>;
  onSelect: (id: string | null) => void;
  onRemove: (child: RegionNode) => void;
}) {
  const refresh = useRefresh();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const missing = new Map(unplaced.map((u) => [u.id, u.reason]));

  const add = useMutation({
    mutationFn: (childName: string) =>
      createRegion({ planetId: tree.planet.id, parentId: parent?.id ?? null, kind, name: childName }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });

  const rename = useMutation({
    mutationFn: (input: { child: RegionNode; name: string }) =>
      updateRegion(input.child.id, { version: input.child.version, name: input.name }),
    onSuccess: refresh,
  });

  const noun = kind === 'city' ? 'city' : kind === 'country' ? 'country' : kind;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-text-muted">
        {PLURAL[kind]} ({items.length})
      </h3>
      <ul className="flex flex-col">
        {items.map((child) => (
          <li key={child.id} className="group flex items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 hover:bg-surface-1">
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: healthColor(child.health.combined) }}
              aria-hidden
            />
            {editing === child.id ? (
              <div className="flex-1">
                <NameInput
                  value={child.name}
                  onSave={(next) => {
                    setEditing(null);
                    rename.mutate({ child, name: next });
                  }}
                />
              </div>
            ) : (
              <button
                onClick={() => onSelect(child.id)}
                onDoubleClick={() => setEditing(child.id)}
                className="flex-1 truncate text-left text-sm"
                title={missing.get(child.id) ?? `Open ${child.name}`}
              >
                {child.name}
                {missing.has(child.id) && <span className="text-text-muted"> · not on the map</span>}
              </button>
            )}
            <span className="text-xs tabular-nums text-text-muted">
              {parent ? `${Math.round(child.share * 100)}%` : ''}
            </span>
            <button
              onClick={() => setEditing(editing === child.id ? null : child.id)}
              className="px-1 text-xs text-text-muted opacity-60 hover:text-text group-hover:opacity-100"
              aria-label={`Rename ${child.name}`}
              title="Rename"
            >
              ✎
            </button>
            <button
              onClick={() => onRemove(child)}
              className="px-1 text-xs text-text-muted opacity-60 hover:text-text group-hover:opacity-100"
              aria-label={`Delete ${child.name}`}
              title="Delete"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = name.trim();
          if (trimmed) add.mutate(trimmed);
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`New ${noun}…`}
          maxLength={120}
          className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1.5 text-sm"
          aria-label={`Name of a new ${noun}`}
        />
        <button
          type="submit"
          disabled={add.isPending || !name.trim()}
          className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-50"
        >
          Add
        </button>
      </form>
      {add.isError && <p className="text-xs text-text-muted">That did not save: {add.error.message}</p>}
    </section>
  );
}

// Moving a place takes everything standing on it — a surah that goes from
// memorising to revising arrives with its building and its history intact.
function MoveSection({ tree, region }: { tree: RegionTree; region: RegionNode }) {
  const refresh = useRefresh();
  const move = useMutation({
    mutationFn: (parentId: string) => moveRegion(region.id, parentId),
    onSuccess: refresh,
  });

  return (
    <Field label="Move it">
      <select
        value=""
        onChange={(e) => e.target.value && move.mutate(e.target.value)}
        disabled={move.isPending}
        className="rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1.5 text-sm"
        aria-label={`Move ${region.name} somewhere else`}
      >
        <option value="">Somewhere else…</option>
        {destinationsFor(tree, region).map((option) => (
          <option key={option.id} value={option.id}>
            {option.path}
          </option>
        ))}
      </select>
    </Field>
  );
}
